import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Button,
  Grid,
  Typography,
  Card,
  CardContent,
  useTheme,
  Skeleton,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReadOnlyFieldGroup from "./readOnlyFields";
import VariantCard from "./variantCard";
import MediaGallery from "./media";
import { isMobile } from "../../globalStyles/mobile";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useEffect, useState } from "react";
import { productService } from "../../utils/services/productService";

const ProductDetailsModal = ({ product, open, onClose }) => {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const theme = useTheme();
  const defaultColor = "#D13438";
  const primary_color = theme.palette?.primary?.main || defaultColor;
  const [productDetails, setProductDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  useEffect(() => {
    setIsLoading(true);
    productService
      .fetch(
        null,
        null,
        `_id[$in][]=${product?._id}&$populate[0][path]=vendor&$populate[0][select][0]=name&$populate[1][path]=client_id&$populate[1][select][0]=business_name&$populate[2][path]=category_id&$populate[2][select][0]=name`,
      )
      .then((res) => {
        if (res && res.data && res.data.length > 0) {
          setProductDetails(res.data[0]);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching product details:", err);
        setIsLoading(false);
      });
  }, [product?._id]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
        },
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          background: `linear-gradient(135deg, ${primary_color} 60%, ${primary_color} 100%)`,
        }}
      >
        <DialogTitle>Product Details</DialogTitle>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </div>
      <Divider style={{ marginBottom: 10 }} />

      <DialogContent
        sx={{
          p: { xs: 1.5, md: 2 },
          overflowY: "auto",
          maxHeight: "80vh",
          "&::-webkit-scrollbar": { width: "6px" },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "#f1f1f1",
            borderRadius: 4,
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#888",
            borderRadius: "4px",
            "&:hover": { backgroundColor: "#555" },
          },
        }}
      >
        {isLoading ? (
          <Stack spacing={2}>
            {/* Skeleton for Basic Information */}
            <Card
              sx={{
                mb: 1.5,
                boxShadow: 0,
                border: "1px solid var(--border-color)",
              }}
            >
              <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                <Skeleton
                  variant="text"
                  width="150px"
                  height={28}
                  sx={{ mb: 2 }}
                />
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <Skeleton
                      variant="text"
                      width="80px"
                      height={20}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="rectangular" height={40} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Skeleton
                      variant="text"
                      width="80px"
                      height={20}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="rectangular" height={40} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Skeleton
                      variant="text"
                      width="80px"
                      height={20}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="rectangular" height={40} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Skeleton
                      variant="text"
                      width="80px"
                      height={20}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="rectangular" height={40} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Skeleton for Description */}
            <Card
              sx={{
                mb: 1.5,
                boxShadow: 0,
                border: "1px solid var(--border-color)",
              }}
            >
              <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                <Skeleton
                  variant="text"
                  width="150px"
                  height={28}
                  sx={{ mb: 2 }}
                />
                <Skeleton variant="rectangular" height={100} />
              </CardContent>
            </Card>

            {/* Skeleton for Media */}
            <Card
              sx={{
                mb: 1.5,
                boxShadow: 0,
                border: "1px solid var(--border-color)",
              }}
            >
              <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                <Skeleton
                  variant="text"
                  width="150px"
                  height={28}
                  sx={{ mb: 2 }}
                />
                <Skeleton variant="rectangular" height={200} />
              </CardContent>
            </Card>

            {/* Skeleton for Pricing & Status */}
            <Card
              sx={{
                mb: 1.5,
                boxShadow: 0,
                border: "1px solid var(--border-color)",
              }}
            >
              <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                <Skeleton
                  variant="text"
                  width="150px"
                  height={28}
                  sx={{ mb: 2 }}
                />
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4}>
                    <Skeleton
                      variant="text"
                      width="80px"
                      height={20}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="rectangular" height={40} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Skeleton
                      variant="text"
                      width="80px"
                      height={20}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="rectangular" height={40} />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Skeleton
                      variant="text"
                      width="80px"
                      height={20}
                      sx={{ mb: 1 }}
                    />
                    <Skeleton variant="rectangular" height={40} />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Skeleton for Variants */}
            <Card
              sx={{
                mb: 1.5,
                boxShadow: 0,
                border: "1px solid var(--border-color)",
              }}
            >
              <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                <Skeleton
                  variant="text"
                  width="150px"
                  height={28}
                  sx={{ mb: 2 }}
                />
                <Skeleton variant="rectangular" height={80} sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" height={80} />
              </CardContent>
            </Card>
          </Stack>
        ) : (
          <>
            {/* Basic Information Section */}
            <Card
              sx={{
                mb: 1.5,
                boxShadow: 0,
                border: "1px solid var(--border-color)",
              }}
            >
              <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                <Typography
                  variant="subtitle1"
                  fontFamily="Poppins"
                  fontWeight={600}
                  color="primary"
                  sx={{ mb: 1 }}
                >
                  Basic Information
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={6}>
                    <ReadOnlyFieldGroup
                      label="Name"
                      value={productDetails?.name}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <ReadOnlyFieldGroup
                      label="Category"
                      value={productDetails?.category_id?.name}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ReadOnlyFieldGroup
                      label="Product Type"
                      value={productDetails?.product_type}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ReadOnlyFieldGroup
                      label="Short Description"
                      value={productDetails?.short_description}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ReadOnlyFieldGroup
                      label="PGA Flag"
                      value={productDetails?.pga_flag}
                    />
                  </Grid>
                  <Grid item xs={6} sm={6}>
                    <ReadOnlyFieldGroup
                      label="URL"
                      value={productDetails?.url}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Description Section */}
            <Card
              sx={{
                mb: 1.5,
                boxShadow: 0,
                border: "1px solid var(--border-color)",
              }}
            >
              <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                <Typography
                  variant="subtitle1"
                  fontFamily="Poppins"
                  fontWeight={600}
                  color="primary"
                  sx={{ mb: 1 }}
                >
                  Description
                </Typography>
                <ReactQuill
                  theme="snow"
                  value={productDetails?.description || ""}
                  readOnly={true}
                  modules={{ toolbar: false }}
                  style={{
                    height: isMobile() ? 60 : "90px",
                    marginTop: 6,
                    color: "var(--text-primary)",
                  }}
                />
                <style>{`
              .ql-container {
                background-color: var(--bg-paper) !important;
                color: var(--text-primary) !important;
                border-radius: 8px;
              }
              .ql-editor {
                min-height: 100%;
                background-color: var(--bg-paper) !important;
                padding: 8px 12px;
                border-radius: 8px;
                color: var(--text-primary) !important;
              }
            `}</style>
              </CardContent>
            </Card>

            {/* Media Section */}
            <Card
              sx={{
                mb: 1.5,
                boxShadow: 0,
                border: "1px solid var(--border-color)",
              }}
            >
              <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                <Typography
                  variant="subtitle1"
                  fontFamily="Poppins"
                  fontWeight={600}
                  color="primary"
                  sx={{ mb: 1 }}
                >
                  Media Gallery
                </Typography>
                <MediaGallery images={productDetails?.images} />
              </CardContent>
            </Card>

            {/* Pricing & Status Section */}
            <Card
              sx={{
                mb: 1.5,
                boxShadow: 0,
                border: "1px solid var(--border-color)",
              }}
            >
              <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                <Typography
                  variant="subtitle1"
                  fontFamily="Poppins"
                  fontWeight={600}
                  color="primary"
                  sx={{ mb: 1 }}
                >
                  Pricing & Status
                </Typography>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4}>
                    <ReadOnlyFieldGroup
                      label="Currency"
                      value={productDetails?.currency}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <ReadOnlyFieldGroup
                      label="Status"
                      value={productDetails?.status}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <ReadOnlyFieldGroup
                      label="Supplier"
                      value={productDetails?.vendor?.name}
                    />
                  </Grid>
                  {user?.role === "admin" && (
                    <Grid item xs={12}>
                      <ReadOnlyFieldGroup
                        label="Client"
                        value={productDetails?.client_id?.business_name}
                      />
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Variants Section */}
            <Card
              sx={{
                mb: 1.5,
                boxShadow: 0,
                border: "1px solid var(--border-color)",
              }}
            >
              <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
                <Typography
                  variant="subtitle1"
                  fontFamily="Poppins"
                  fontWeight={600}
                  color="primary"
                  sx={{ mb: 1 }}
                >
                  Variants
                </Typography>
                {productDetails?.variants
                  ?.filter((v) => !v.parent)
                  .map((variant) => (
                    <VariantCard
                      key={variant.uid}
                      variant={variant}
                      allVariants={productDetails.variants}
                      productId={productDetails._id}
                      currency={productDetails?.currency}
                      // Falls back to the selling currency: `cost_currency` is
                      // only set by providers that state one (Pipe17's
                      // `costCurrency`), and an absent code would otherwise
                      // render the default "$" on a non-USD product.
                      costCurrency={
                        productDetails?.cost_currency ||
                        productDetails?.currency
                      }
                    />
                  ))}
              </CardContent>
            </Card>
          </>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          p: 2,
          backgroundColor: "var(--bg-secondary)",
          borderTop: "1px solid var(--border-color)",
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          color="primary"
          sx={{
            textTransform: "none",
            borderRadius: 2,
            px: 3,
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductDetailsModal;
